import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AtivoLog {
  id: string;
  ativo_id: string;
  usuario_id?: string;
  acao: string;
  campo_alterado?: string;
  valor_anterior?: string;
  valor_novo?: string;
  tipo: string;
  criado_em: string;
  usuario?: {
    id: string;
    name: string;
  };
}

export interface AtivoLogsFilters {
  usuarioId?: string;
  campoAlterado?: string;
  dataInicio?: string;
  dataFim?: string;
}

export const useAtivoLogs = (ativoId: string, filters?: AtivoLogsFilters) => {
  return useQuery({
    queryKey: ['ativo-logs', ativoId, filters],
    queryFn: async () => {
      
      let query = supabase
        .from('cmdb_ativos_logs')
        .select('*')
        .eq('ativo_id', ativoId);

      // Aplicar filtros
      if (filters?.usuarioId) {
        query = query.eq('usuario_id', filters.usuarioId);
      }

      if (filters?.campoAlterado) {
        query = query.eq('campo_alterado', filters.campoAlterado);
      }

      if (filters?.dataInicio) {
        query = query.gte('criado_em', filters.dataInicio);
      }

      if (filters?.dataFim) {
        query = query.lte('criado_em', filters.dataFim);
      }

      const { data: logs, error } = await query.order('criado_em', { ascending: false });

      if (error) {
        console.error('Error fetching ativo logs:', error);
        throw error;
      }

      // Buscar informações dos usuários separadamente
      const userIds = logs
        ?.map(log => log.usuario_id)
        .filter((id): id is string => id !== null && id !== undefined) || [];

      const uniqueUserIds = [...new Set(userIds)];
      
      let usersMap: Record<string, { id: string; name: string }> = {};
      
      if (uniqueUserIds.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('id, name')
          .in('id', uniqueUserIds);
        
        if (users) {
          usersMap = users.reduce((acc, user) => {
            acc[user.id] = user;
            return acc;
          }, {} as Record<string, { id: string; name: string }>);
        }
      }

      // Combinar logs com informações de usuários
      const logsWithUsers: AtivoLog[] = logs?.map(log => ({
        ...log,
        usuario: log.usuario_id ? usersMap[log.usuario_id] : undefined,
      })) || [];

      return logsWithUsers;
    },
    enabled: !!ativoId,
  });
};
